import React, {useCallback, useEffect, useRef, useState} from "react";
import Typography from "@mui/material/Typography";
import {Form} from "../../form/Form/Form";
import {useTranslation} from "react-i18next";
import '../entity-form.scss';
import {useToastContext} from "../../../context/toastContext";
import {BaseEntityNameLess} from "../../../api/entities/BaseEntity";
import {MetadataPropertyEntity} from "../../../api/entities/replancity_MetadataProperty";
import FieldCreator from "../../form/FieldCreator";
import {useTheme} from "../../../context/themeContext";
import {getFilledFieldsObjOnFormSubmit} from "../../../utils/utils";
import {DeleteIcon} from "../../icons/icons/DeleteIcon";
import {IconButton} from "../../buttons/IconButton/IconButton";
import DeleteEntityModal from "../DeleteEntityModal/DeleteEntityModal";
import {EntityMetadata} from "../../../hooks/entites/useEntityLoader";


export type FormDataObject = { [key: string]: string | number };

export type EntityFormMetadataProperty = Omit<MetadataPropertyEntity, 'showIf' | 'mandatoryIf'> & {
    // eslint-disable-next-line @typescript-eslint/ban-types
    showIf: Function;
    // eslint-disable-next-line @typescript-eslint/ban-types
    mandatoryIf: Function;
};

interface Props {
    loading: boolean;
    entity: BaseEntityNameLess; //remove?
    metadata: EntityMetadata | undefined;
    changeFormFn: (formDataObj: FormDataObject) => void;
    saveFn: <T, >(formDataObj: FormDataObject, reloadRequired?: boolean) => Promise<void>;
    deleteFn?: ({entity}: { entity: BaseEntityNameLess }) => Promise<void>;
    icons?: JSX.Element | JSX.Element[];
}

const EntityForm = ({
                        loading,
                        entity,
                        metadata,
                        changeFormFn,
                        saveFn,
                        deleteFn,
                        icons,
                        ...props
                    }: Props) => {
    const [formDisabled, setFormDisabled] = useState<boolean>(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);
    const formRef = useRef<HTMLFormElement>(null);
    const {addToast} = useToastContext();
    const {t} = useTranslation();
    const {theme} = useTheme();

    useEffect(() => {
        setFormDisabled(!!formRef.current && !formRef.current.checkValidity());
    }, [metadata, entity])

    const onFormSubmit = useCallback(async (event: React.FormEvent<HTMLFormElement>, skipValidation = false) => {
        const {currentTarget} = event;

        const formDataObj = getFilledFieldsObjOnFormSubmit(event);

        if (!skipValidation && !currentTarget.reportValidity()) {
            addToast(`${t('common.not-all-required-fields-are-filled')}`, 'error');
            return;
        }

        setFormDisabled(true);
        await saveFn(formDataObj);
        setFormDisabled(false);
    }, [saveFn, addToast, t]);

    const formChangeHandler = useCallback(async (event) => {
        const {target, currentTarget} = event;

        if (metadata?.metadataWithSaveAndUpdateOnChange[target.name]) {
            await onFormSubmit(event, true);
            return;
        }

        setFormDisabled(!currentTarget.checkValidity());

        const formDataObj = getFilledFieldsObjOnFormSubmit(event);
        changeFormFn(formDataObj);
    }, [changeFormFn, metadata]);

    const getFormFields = (metaData: EntityFormMetadataProperty[], entity: any) => {
        return metaData.map((metadataEntity, ind) => {
                const {name, showIf, mandatoryIf} = metadataEntity;
                const value = entity[name];
                const toShowField = showIf.apply(entity);
                const required = mandatoryIf.apply(entity);

                return (
                    <FieldCreator
                        key={ind}
                        value={value}
                        toShowField={toShowField}
                        required={required}
                        metadata={metadataEntity}
                        autoFocus={ind === 0}
                    />
                )
            }
        )
    }

    return (
        <div className={`entity-editor ${theme}`} data-testid="entityEditor">
            <div className="entity-editor__header">
                <div className="title">
                    <Typography
                        sx={{
                            fontSize: '14px',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis'
                        }}
                    >
                        {loading
                            ? null
                            : metadata?.visibleMetadata.length ? entity?.name : t('map.no-editable-fields')}
                    </Typography>
                </div>
                <div className="actions">
                    {
                        icons
                            ? icons
                            : null
                    }
                    {
                        entity.id && deleteFn ?
                            <IconButton
                                icon={DeleteIcon}
                                width={20}
                                height={20}
                                onClick={() => setShowDeleteConfirmation(true)}
                                borderless
                                testId="entityFormDeleteBtn"
                            />
                            : null
                    }
                    {/*{props.summaryIcon?.(entity?.id)}*/}
                </div>
            </div>
            {
                metadata?.visibleMetadata.length
                    ? <Form
                        name="entityForm"
                        onSubmit={onFormSubmit}
                        onChange={formChangeHandler}
                        submitBtnTitle={t('common.save')}
                        disabled={formDisabled}
                        ref={formRef}
                        submitBtnTestId="entityFormSubmitBtn"
                        testId="entityForm"
                    >
                        {getFormFields(metadata.visibleMetadata, entity)}
                    </Form>
                    : null
            }
            {
                showDeleteConfirmation && deleteFn
                    ? <DeleteEntityModal
                        entity={entity}
                        setModalShow={setShowDeleteConfirmation}
                        deleteFn={deleteFn}
                    />
                    : null
            }
        </div>
    )
}

export default React.memo(EntityForm);